import React, { useEffect, useState } from "react";

import styled, { keyframes } from "styled-components";
import { Container, Row, Col } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";

import { D1, GradientSpan, P3 } from "../../../components/theme/text/Headings";

import {
  Blue,
  OffWhite,
  DarkGrey,
  gradientColors,
  Purple,
  White,
  Pink,
  White05,
} from "../../../components/theme/colors";
import { TextField } from "@mui/material";
import { ButtonGradient } from "../../../components/theme/Button";
import { useAuth } from "../../../services/AuthContext";
import { db } from "../../../services/firebase.config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import mixpanelService from "../../../services/MixpanelService";
import postHogService from "../../../services/PostHogService";
import {
  ProgressBarContainer,
  ProgressBarProgress,
} from "../../../components/theme/QuizBox";
import LayoutOnboarding from "../../../components/layout/layoutOnboarding";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const TopContent = styled.div`
  padding: 0 15px;
  animation: ${fadeInUp} 0.7s ease-out;
`;

const HeaderRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 0px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const BottomLogin = styled.div`
  position: absolute;
  bottom: 10vh;
  left: 2vh;
  right: 2vh;
`;

const OnboardingOne = () => {
  const navigate = useNavigate();
  const { currentUserState } = useAuth();
  const [displayName, setDisplayName] = useState("");

  // Track page view on mount
  useEffect(() => {
    const props = {
      user_id: currentUserState?.uid || "anonymous",
      step: "name",
      timestamp: new Date().toISOString(),
    };
    mixpanelService.track("Onboarding Name Viewed", props);
    postHogService.track("Onboarding Name Viewed", props);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!currentUserState) return;

      try {
        const profileDocRef = doc(db, "public-profiles", currentUserState.uid);
        const profileDoc = await getDoc(profileDocRef);

        if (profileDoc.exists()) {
          const data = profileDoc.data();
          setDisplayName(data.displayName || "");
        }
      } catch (error) {
        // Silently fail - profile data is optional during onboarding
      }
    };

    fetchProfileData();
  }, [currentUserState]);

  const handleBack = async () => {
    navigate("/onboarding-intro");
  };

  const handleUpdateProfile = async () => {
    if (!currentUserState) {
      alert("You need to be logged in to update your profile");
      return;
    }

    const profileDocRef = doc(db, "public-profiles", currentUserState.uid);

    try {
      await setDoc(
        profileDocRef,
        {
          uid: currentUserState.uid,
          displayName: displayName,
        },
        { merge: true } // Merge to add to the same document
      );

      navigate("/onboarding-one-twenty-five");
    } catch (error) {
      console.error("Error adding answer: ", error);
    }
  };
  return (
    <LayoutOnboarding>
      <ProgressBarContainer style={{ marginBottom: 8 }}>
        <ProgressBarProgress
          $background={Blue}
          $transitionBackground={Purple}
          width={`20%`}
        />
      </ProgressBarContainer>
      <HeaderRow>
        <div
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "40px",
            height: "40px",
          }}
          onClick={handleBack}
        >
          <ArrowCircleLeftIcon
            style={{
              color: White05,
              fontSize: "28px",
            }}
          />
        </div>
      </HeaderRow>
      <Container>
        <Row>
          <Col
            lg={{ offset: 4, span: 4 }}
            md={{ offset: 3, span: 6 }}
            sm={{ offset: 2, span: 8 }}
            xs={{ span: 12 }}
            className="text-align-center"
          >
            <TopContent>
              <D1 color={White} className="mt-40 mb-40">
                What's your{" "}
                <GradientSpan $background={Pink} $transitionBackground={Blue}>
                  name?
                </GradientSpan>
              </D1>
              <TextField
                fullWidth
                label="your name"
                variant="outlined"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "17px",
                    color: OffWhite,
                    background: DarkGrey,
                    borderRadius: "15px",
                    padding: "15px 30px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "17px",
                    color: OffWhite,
                    padding: "0px 20px",
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "transparent",
                    },
                    "&:hover fieldset": {
                      borderColor: "transparent",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "transparent",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                  },
                  "& .Mui-focused": {
                    color: OffWhite, // focus label
                    borderRadius: "15px",
                  },
                }}
              />
              <P3
                color={White05}
                className="mt-30"
                style={{ textAlign: "center" }}
              >
                By continuing, you agree to our{" "}
                <a
                  href="https://www.vibecheckme.com/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: White05,
                    textDecoration: "underline",
                  }}
                >
                  Terms of Service
                </a>{" "}
                <br />
                and{" "}
                <a
                  href="https://www.vibecheckme.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: White05,
                    textDecoration: "underline",
                  }}
                >
                  Privacy Policy
                </a>
                .
              </P3>
            </TopContent>
            <BottomLogin>
              <div>
                <ButtonGradient
                  width="90%"
                  className="mt-20"
                  $background={gradientColors["personality-check"].background}
                  $transitionBackground={
                    gradientColors["personality-check"].transitionBackground
                  }
                  onClick={handleUpdateProfile}
                >
                  Continue
                </ButtonGradient>
              </div>
            </BottomLogin>
          </Col>
        </Row>
      </Container>
    </LayoutOnboarding>
  );
};

export default OnboardingOne;
